import { useState, useEffect, useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Chip, TextField, styled } from '@mui/material'
import { CreatableAutocomplete } from '@carto/meridian-ds/components'
import useTags from 'features/tags/hooks/useTags'
import TagColor from './TagColor'

interface TagsAutocompleteProps {
  selectedTags: string[]
  onChange: (tags: string[]) => void
}

const CustomAutocomplete = styled(CreatableAutocomplete)(({ theme }) => ({
  '.MuiAutocomplete-tag:not(.MuiButtonBase-root) ': {
    background: theme.palette.common.white,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: theme.palette.default.outlinedBorder
  }
}))

function TagsAutocomplete({ selectedTags, onChange }: TagsAutocompleteProps) {
  const intl = useIntl()
  const { data: remoteTags, isFetched } = useTags()
  const [tags, setTags] = useState(remoteTags)

  useEffect(() => {
    if (isFetched) {
      setTags(remoteTags)
    }
  }, [isFetched, remoteTags])

  const formattedTags = useMemo(() => {
    return [
      { title: intl.formatMessage({ id: 'tags.helper' }), subtitle: true },
      ...(tags?.map((t) => ({ title: t, startAdornment: <TagColor name={t} /> })) || [])
    ]
  }, [tags, intl])

  const handleOnChangeTags = (e, value) => {
    const mapTags = value.map((tag) => {
      if (typeof tag === 'string') return tag
      return tag.inputValue || tag.title
    })

    setTags([...new Set([...(remoteTags || []), ...mapTags])])
    onChange(mapTags)
  }

  const isOptionEqualToValue = (option, value) => {
    return option?.title === value
  }

  return (
    <CustomAutocomplete
      limitTags={4}
      value={selectedTags}
      options={formattedTags}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      getOptionLabel={(option: any) => option?.title}
      isOptionEqualToValue={isOptionEqualToValue}
      renderTags={(tags, getTagProps) =>
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        tags.map((tag: any, index) => {
          const tagStr = typeof tag === 'string' ? tag : tag.title || tag.inputValue

          return (
            <Chip
              variant="outlined"
              size="small"
              color="default"
              label={tagStr}
              {...(tagStr && typeof tagStr === 'string' && { avatar: <TagColor name={tagStr} /> })}
              {...getTagProps({ index })}
            />
          )
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            maxLength: 50
          }}
          label={intl.formatMessage({ id: 'tags.label' })}
          placeholder={intl.formatMessage({ id: 'tags.placeholder' })}
          InputLabelProps={{ shrink: true }}
        />
      )}
      newItemLabel={(value) => <FormattedMessage id="tags.create" values={{ value }} />}
      onChange={handleOnChangeTags}
      multiple
    />
  )
}

export default TagsAutocomplete
